import { FormikContext } from 'formik'
import { FC, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanyConfigForm } from './company-config.interface'
import { CompanyConfigEmployeesComponent } from './components/company-config-employees.component'
import { CompanyConfigYesNoComponent } from './components/company-config-yes-no.component'
import { getCompanyConfigInitialValues } from './helpers/get-company-config-initial-values'
import { CompanyEmployeesConfigType } from './types/company-config'
import { CompanyConfigValidationSchema } from './validators/company-config-validation-schema'
import { useFormik } from '../../../../../common/hooks/formik'
import { PureInput } from '../../../../../common/ui/pure-input/pure-input'

type CompanyConfigProps = {
  initialValue?: CompanyConfigForm
}

export const CompanyConfig: FC<CompanyConfigProps> = forwardRef(({ initialValue }: CompanyConfigProps, ref) => {
  const { t } = useTranslation()
  const formik = useFormik<CompanyConfigForm>({
    initialValues: getCompanyConfigInitialValues(initialValue),
    validationSchema: CompanyConfigValidationSchema(),
    onSubmit: () => {},
  })

  const handleStateChange = (state: boolean, stateKey: string) => {
    formik.setFieldValue(stateKey, state)
  }

  const handleCompanyStateChange = (state: CompanyEmployeesConfigType) => {
    formik.setFieldValue('employees', JSON.stringify(state))
  }

  useImperativeHandle(ref, () => ({
    getValues: () => {
      return {
        errors: formik.validateForm(),
        values: formik.values,
      }
    },
  }))

  return (
    <FormikContext.Provider value={formik as any}>
      <h2 className="mt-3 mb-5">{t('onboarding.companyConfig.header')}</h2>
      <div className="row mb-2 g-0 w-100">
        <PureInput
          {...formik.getFieldProps('name')}
          error={formik.errors.name}
          label={t('onboarding.companyConfig.nameLabel')}
          moduleStyle="mb-3"
          placeholder={t('onboarding.companyConfig.namePlaceholder')}
          required={false}
        />
      </div>
      <div className="row g-0 gx-3 mb-4">
        <CompanyConfigYesNoComponent
          error={formik.errors.departments}
          initialState={initialValue?.departments}
          majorText={t('onboarding.companyConfig.departments.majorText')}
          minorText={t('onboarding.companyConfig.departments.minorText')}
          onStateChange={(state: boolean) => handleStateChange(state, 'departments')}
        />
        <CompanyConfigYesNoComponent
          error={formik.errors.projects}
          initialState={initialValue?.projects}
          majorText={t('onboarding.companyConfig.projects.majorText')}
          minorText={t('onboarding.companyConfig.projects.minorText')}
          onStateChange={(state: boolean) => handleStateChange(state, 'projects')}
        />
        <CompanyConfigEmployeesComponent
          error={formik.errors.employees}
          initialState={initialValue?.employees}
          majorText={t('onboarding.companyConfig.employees.majorText')}
          minorText={t('onboarding.companyConfig.employees.minorText')}
          noteText={t('onboarding.companyConfig.employees.noteText')}
          onStateChange={handleCompanyStateChange}
        />
      </div>
    </FormikContext.Provider>
  )
})
